import { config } from '../config';

/**
 * Cognito authorization-code flow with PKCE.
 *
 * This app is a public client — it ships no secret — so PKCE is what stops an
 * intercepted authorization code from being redeemed by anyone else. Token
 * exchange goes straight to Cognito's own endpoint; it echoes the caller's Origin
 * back in Access-Control-Allow-Origin, so no backend proxy is needed.
 *
 * Tokens live in sessionStorage: cleared when the tab closes, and never shared
 * across tabs. That still leaves them reachable from injected script, so the
 * hardening step after this is a cookie-based backend-for-frontend.
 */

const VERIFIER_KEY = 'shipdesk.pkce_verifier';
const STATE_KEY = 'shipdesk.oauth_state';
const TOKENS_KEY = 'shipdesk.tokens';

/** Refresh this many seconds before actual expiry to avoid racing a request. */
const REFRESH_MARGIN_S = 120;

type StoredTokens = {
  accessToken: string;
  refreshToken?: string;
  expiresAt: number;
};

function base64UrlEncode(bytes: Uint8Array): string {
  let binary = '';
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function randomUrlSafe(byteLength: number): string {
  const bytes = new Uint8Array(byteLength);
  crypto.getRandomValues(bytes);
  return base64UrlEncode(bytes);
}

async function codeChallenge(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  return base64UrlEncode(new Uint8Array(digest));
}

function readTokens(): StoredTokens | null {
  const raw = sessionStorage.getItem(TOKENS_KEY);
  if (!raw) {
    return null;
  }
  try {
    return JSON.parse(raw) as StoredTokens;
  } catch {
    sessionStorage.removeItem(TOKENS_KEY);
    return null;
  }
}

function writeTokens(tokens: StoredTokens): void {
  sessionStorage.setItem(TOKENS_KEY, JSON.stringify(tokens));
}

export function signOut(): void {
  sessionStorage.removeItem(TOKENS_KEY);
  sessionStorage.removeItem(VERIFIER_KEY);
  sessionStorage.removeItem(STATE_KEY);
}

export function isSignedIn(): boolean {
  return readTokens() !== null;
}

/** Sends the browser to the Cognito hosted UI. Does not return. */
export async function beginSignIn(): Promise<void> {
  const verifier = randomUrlSafe(64);
  const state = randomUrlSafe(16);

  sessionStorage.setItem(VERIFIER_KEY, verifier);
  sessionStorage.setItem(STATE_KEY, state);

  const params = new URLSearchParams({
    response_type: 'code',
    client_id: config().cognitoClientId,
    redirect_uri: config().redirectUri,
    scope: 'openid email profile',
    state,
    code_challenge: await codeChallenge(verifier),
    code_challenge_method: 'S256',
  });

  window.location.assign(`https://${config().cognitoDomain}/oauth2/authorize?${params}`);
}

type TokenResponse = {
  access_token?: string;
  refresh_token?: string;
  expires_in?: number;
  error?: string;
  error_description?: string;
};

async function exchange(body: Record<string, string>): Promise<StoredTokens> {
  const response = await fetch(`https://${config().cognitoDomain}/oauth2/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams(body),
  });

  const data = (await response.json()) as TokenResponse;
  if (!data.access_token) {
    throw new Error(data.error_description ?? data.error ?? 'Inloggen is mislukt');
  }

  return {
    accessToken: data.access_token,
    refreshToken: data.refresh_token,
    expiresAt: Date.now() + (data.expires_in ?? 3600) * 1000,
  };
}

/**
 * Completes the redirect leg if the URL carries an authorization code.
 * Returns true when a sign-in was completed on this page load.
 */
export async function completeSignIn(): Promise<boolean> {
  const params = new URLSearchParams(window.location.search);
  const code = params.get('code');
  const returnedState = params.get('state');
  const error = params.get('error');

  if (error) {
    // Clear the query string so a refresh does not replay the failure.
    window.history.replaceState({}, '', window.location.pathname);
    throw new Error(params.get('error_description') ?? error);
  }

  if (!code) {
    return false;
  }

  const verifier = sessionStorage.getItem(VERIFIER_KEY);
  const expectedState = sessionStorage.getItem(STATE_KEY);
  window.history.replaceState({}, '', window.location.pathname);

  if (!verifier) {
    throw new Error('Inlogsessie is verlopen. Probeer opnieuw in te loggen.');
  }
  if (!expectedState || returnedState !== expectedState) {
    throw new Error('Inlogverzoek kon niet worden gevalideerd. Probeer opnieuw in te loggen.');
  }

  const tokens = await exchange({
    grant_type: 'authorization_code',
    client_id: config().cognitoClientId,
    redirect_uri: config().redirectUri,
    code,
    code_verifier: verifier,
  });

  sessionStorage.removeItem(VERIFIER_KEY);
  sessionStorage.removeItem(STATE_KEY);
  writeTokens(tokens);
  return true;
}

let refreshInFlight: Promise<string> | null = null;

/**
 * Returns a valid access token, refreshing first if it is close to expiry.
 * Concurrent callers share one refresh so a burst does not spend the refresh
 * token several times over.
 */
export async function getAccessToken(): Promise<string> {
  const tokens = readTokens();
  if (!tokens) {
    throw new Error('Niet ingelogd');
  }

  if (Date.now() < tokens.expiresAt - REFRESH_MARGIN_S * 1000) {
    return tokens.accessToken;
  }

  if (!tokens.refreshToken) {
    signOut();
    throw new Error('Sessie verlopen. Log opnieuw in.');
  }

  refreshInFlight ??= exchange({
    grant_type: 'refresh_token',
    client_id: config().cognitoClientId,
    refresh_token: tokens.refreshToken,
  })
    .then((refreshed) => {
      // Cognito omits refresh_token on a refresh response; keep the existing one.
      const merged: StoredTokens = {
        ...refreshed,
        refreshToken: refreshed.refreshToken ?? tokens.refreshToken,
      };
      writeTokens(merged);
      return merged.accessToken;
    })
    .catch((error: unknown) => {
      signOut();
      throw error;
    })
    .finally(() => {
      refreshInFlight = null;
    });

  return refreshInFlight;
}

/** fetch wrapper that attaches a fresh bearer token. */
export async function authorizedFetch(url: string, init: RequestInit = {}): Promise<Response> {
  const token = await getAccessToken();
  const headers = new Headers(init.headers);
  headers.set('Authorization', `Bearer ${token}`);
  return fetch(url, { ...init, headers });
}
